import { CarIcon, GearIcon, RulerIcon, TruckIcon } from "@phosphor-icons/react";
import { ComponentProps, useEffect } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import { z } from "zod";

import { GLOBAL_SCHEMA } from "@/lib/zod";

import { Input } from "../Input";

const _schema = z.object({
  truck: z.object({ ...GLOBAL_SCHEMA.TRUCK.shape, owner: z.string().optional() })
});

// Ajuste final: OmitUnnecessary não precisa ser genérico, apenas Omit<ComponentProps<any>, ...>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OmitUnnecessary = Omit<ComponentProps<any>, "name" | "control">;
type TruckFieldsProps = {
  brands: {
    isLoading: boolean;
    isError: boolean;
    data: OptionData[];
  };
  models: {
    isLoading: boolean;
    isError: boolean;
    data: OptionData[];
  };
  implementsTypes: OptionData[];
  children?: React.ReactNode;
  slot?: {
    plate?: {
      props: OmitUnnecessary;
    };
    renavam?: {
      props: OmitUnnecessary;
    };
    brand?: {
      props: OmitUnnecessary;
    };
    model?: {
      props: OmitUnnecessary;
    };
    color?: {
      props: OmitUnnecessary;
    };
    implementType?: {
      props: OmitUnnecessary;
    };
    shaftQuantity?: {
      props: OmitUnnecessary;
    };
    grossCapacity?: {
      props: OmitUnnecessary;
    };
    netCapacity?: {
      props: OmitUnnecessary;
    };
  };
};

export function TruckFields({ brands, models, implementsTypes, children, slot }: TruckFieldsProps) {
  const { control, watch, trigger } = useFormContext<FieldValues>();

  const brand = watch("truck.brand");
  const model = watch("truck.model");

  const brandDisabled = brands.isLoading || brands.isError;
  const modelDisabled = !brand || models.isLoading || models.isError;

  useEffect(() => {
    if (!model) return;

    trigger("truck.color");
  }, [model]);

  return (
    <div className="flex flex-col gap-4">
      {children}

      <div className="flex flex-col gap-[inherit] *:flex-1 md:flex-row">
        <Input.Text
          control={control}
          leftSection={<CarIcon weight="bold" />}
          maskProps={{ mask: "*******" }}
          placeholder="Placa (Apenas letras e números)"
          name="truck.plate"
          {...slot?.plate?.props}
        />

        <Input.Text
          control={control}
          leftSection={<TruckIcon weight="bold" />}
          maskProps={{ mask: "999999999-99" }}
          placeholder="Renavam"
          name="truck.renavam"
          {...slot?.renavam?.props}
        />
      </div>

      <Input.Select control={control} data={brands.data} placeholder="Marca" name="truck.brand" disabled={brandDisabled} {...slot?.brand?.props} />

      <Input.Select control={control} data={models.data} placeholder="Modelo" name="truck.model" disabled={modelDisabled} {...slot?.model?.props} />

      <Input.Color name="truck.color" placeholder="Cor" control={control} {...slot?.color?.props} />

      <Input.Select control={control} data={implementsTypes} placeholder="Tipo de implemento" name="truck.implementType" {...slot?.implementType?.props} />

      <div className="flex flex-col gap-[inherit] *:flex-1 md:flex-row">
        <Input.Number
          control={control}
          leftSection={<GearIcon weight="bold" />}
          placeholder="Quantidade de eixos"
          name="truck.shaftQuantity"
          defaultValue={0}
          {...slot?.shaftQuantity?.props}
        />

        <Input.Number
          control={control}
          leftSection={<RulerIcon weight="bold" />}
          placeholder="Capacidade bruta em tonelada"
          name="truck.grossCapacity"
          defaultValue={0}
          {...slot?.grossCapacity?.props}
        />

        <Input.Number
          control={control}
          leftSection={<RulerIcon weight="bold" />}
          placeholder="Capacidade líquida em tonelada"
          name="truck.netCapacity"
          defaultValue={0}
          {...slot?.netCapacity?.props}
        />
      </div>
    </div>
  );
}
